import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { LogOut, Plus, Sparkles, Trash2, Save, X, Upload, ImageOff } from "lucide-react";
import { toast } from "sonner";
import apiClient, { API, setAdminToken } from "@/lib/api";
import { IMAGES } from "@/data/images";

const EMPTY_ARTICLE = {
  slug: "",
  category: "general",
  cover_image: "",
  published: true,
  translations: { fr: { title: "", meta_title: "", meta_description: "", excerpt: "", content: "" }, en: { title: "", meta_title: "", meta_description: "", excerpt: "", content: "" } },
};

export default function AdminDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [topic, setTopic] = useState("");
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("fastority_admin_token");
    if (!token) {
      navigate("/admin/login");
      return;
    }
    apiClient
      .get("/auth/me")
      .then(() => load())
      .catch(() => {
        setAdminToken(null);
        navigate("/admin/login");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = () => {
    apiClient.get("/admin/blog").then((res) => setArticles(res.data)).catch(() => {});
  };

  const logout = () => {
    setAdminToken(null);
    navigate("/admin/login");
  };

  const generate = async () => {
    if (!topic) return;
    setGenerating(true);
    try {
      const res = await apiClient.post("/blog/ai-generate", { topic, category: "general", langs: ["fr", "en"] });
      setEditing({ slug: res.data.slug, category: res.data.category, published: true, translations: res.data.translations });
      toast.success("Article généré avec succès");
    } catch {
      toast.error("Génération IA échouée");
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing.id) {
        await apiClient.put(`/blog/${editing.id}`, editing);
      } else {
        await apiClient.post("/blog", editing);
      }
      toast.success("Article enregistré");
      setEditing(null);
      load();
    } catch {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await apiClient.delete(`/blog/${id}`);
    load();
  };

  const uploadCoverImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await apiClient.post("/blog/upload-image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEditing((prev) => ({ ...prev, cover_image: res.data.cover_image }));
      toast.success("Photo uploadée avec succès");
    } catch {
      toast.error("Échec de l'upload de la photo");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div data-testid="admin-dashboard-page" className="min-h-screen bg-offwhite">
      <header className="bg-slate900 text-white px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <img src={IMAGES.logo} alt="Fastority" className="h-7 w-7" />
          <span className="font-heading font-bold">{t("admin.dashboard_title")}</span>
        </div>
        <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-1.5 text-sm text-white/70 hover:text-white">
          <LogOut size={15} /> {t("admin.logout")}
        </button>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="bg-white rounded-2xl p-6 border border-gray-100 mb-8 flex flex-wrap gap-3 items-center">
          <input
            data-testid="admin-ai-topic-input"
            placeholder="Sujet de l'article (ex: Sécurité en covoiturage)"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            className="flex-1 min-w-[240px] rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:border-deepblue"
          />
          <button onClick={generate} disabled={generating} data-testid="admin-ai-generate-button" className="inline-flex items-center gap-2 bg-deepblue text-white font-semibold px-5 py-2.5 rounded-full text-sm disabled:opacity-60">
            <Sparkles size={15} /> {generating ? "Génération..." : t("admin.ai_generate")}
          </button>
          <button onClick={() => setEditing(EMPTY_ARTICLE)} data-testid="admin-new-article-button" className="inline-flex items-center gap-2 bg-coral text-white font-semibold px-5 py-2.5 rounded-full text-sm">
            <Plus size={15} /> {t("admin.new_article")}
          </button>
        </div>

        {editing && (
          <div data-testid="admin-article-editor" className="bg-white rounded-2xl p-6 border border-gray-100 mb-8 space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-heading font-bold text-lg text-slate900">Éditer l&apos;article</h3>
              <button onClick={() => setEditing(null)} data-testid="admin-editor-close"><X size={18} /></button>
            </div>
            <input data-testid="admin-editor-slug" placeholder="slug" value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm" />

            <div className="flex items-center gap-4">
              <div className="w-32 h-20 rounded-xl bg-deepblue/10 flex items-center justify-center overflow-hidden shrink-0 border border-gray-200">
                {editing.cover_image ? (
                  <img src={editing.cover_image} alt="Cover" className="w-full h-full object-cover" data-testid="admin-editor-cover-preview" />
                ) : (
                  <ImageOff className="text-deepblue/30" size={20} />
                )}
              </div>
              <label htmlFor="cover-upload" data-testid="admin-editor-cover-upload-label" className="inline-flex items-center gap-2 bg-deepblue text-white font-semibold px-4 py-2.5 rounded-full text-sm cursor-pointer hover:bg-deepblue/90 transition-colors">
                <Upload size={14} /> {uploading ? "Envoi..." : "Uploader une photo"}
                <input id="cover-upload" data-testid="admin-editor-cover-upload-input" type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={uploadCoverImage} disabled={uploading} className="hidden" />
              </label>
              {editing.cover_image && (
                <button type="button" onClick={() => setEditing({ ...editing, cover_image: "" })} data-testid="admin-editor-cover-remove" className="text-coral text-sm font-semibold">
                  Retirer
                </button>
              )}
            </div>

            {["fr", "en"].map((lg) => (
              <div key={lg} className="border-t border-gray-100 pt-4">
                <span className="text-xs uppercase font-bold text-coral">{lg}</span>
                <input
                  data-testid={`admin-editor-title-${lg}`}
                  placeholder="Titre"
                  value={editing.translations[lg]?.title || ""}
                  onChange={(e) => setEditing({ ...editing, translations: { ...editing.translations, [lg]: { ...editing.translations[lg], title: e.target.value } } })}
                  className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm mt-2"
                />
                <textarea
                  data-testid={`admin-editor-content-${lg}`}
                  placeholder="Contenu HTML"
                  rows={5}
                  value={editing.translations[lg]?.content || ""}
                  onChange={(e) => setEditing({ ...editing, translations: { ...editing.translations, [lg]: { ...editing.translations[lg], content: e.target.value } } })}
                  className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm mt-2"
                />
              </div>
            ))}
            <button onClick={save} disabled={saving} data-testid="admin-editor-save" className="inline-flex items-center gap-2 bg-emeraldgreen text-white font-semibold px-5 py-2.5 rounded-full text-sm disabled:opacity-60">
              <Save size={15} /> Enregistrer
            </button>
          </div>
        )}

        <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-100">
          {articles.map((a) => (
            <div key={a.id} data-testid={`admin-article-row-${a.slug}`} className="flex items-center justify-between px-6 py-4">
              <div>
                <p className="font-semibold text-slate900">{a.title}</p>
                <p className="text-xs text-slate-500">{a.slug} · {a.category}</p>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setEditing(a)} data-testid={`admin-edit-${a.slug}`} className="text-deepblue text-sm font-semibold">Éditer</button>
                <button onClick={() => remove(a.id)} data-testid={`admin-delete-${a.slug}`} className="text-coral"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
